/**
 * CetusEd Admin Console — Cross-District Audit Log
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(B) — All vendor support staff actions
 *   across districts must be logged and available for audit. This view
 *   shows the cross-district audit trail without requiring entry of a
 *   specific district ID.
 *
 *   The audit log is immutable. Each entry is hash-chained to detect
 *   tampering. Filters allow scoping by district, action type, and date.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuditLog } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { AdminAuditEntry } from '../../api/hooks';

export const AuditLog: React.FC = () => {
  const [actionFilter] = useState('');
  const { data: entries, isLoading, error } = useAdminAuditLog({
    ...(actionFilter ? { action: actionFilter } : {}),
    limit: 200,
  });

  const columns: Column<AdminAuditEntry>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (row) => new Date(row.timestamp).toLocaleString(),
    },
    { key: 'districtId', header: 'District', sortable: true },
    { key: 'actorEmail', header: 'Actor', sortable: true },
    { key: 'action', header: 'Action', sortable: true },
    { key: 'resource', header: 'Resource' },
    { key: 'ipAddress', header: 'IP', width: '140px' },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load audit log.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/monitoring">Monitoring</Link> / Audit Log
      </nav>

      <div className="page-header">
        <h1>Cross-District Audit Log</h1>
      </div>

      <DataTable
        columns={columns}
        data={entries || []}
        keyExtractor={(e) => e.id}
        isLoading={isLoading}
        emptyMessage="No audit entries found."
        pageSize={25}
      />
    </div>
  );
};
